import React, { useCallback, useRef, useEffect } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { TagManager } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Save, Loader2 } from 'lucide-react';
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getCachedTemplates } from '@/lib/stores/templatesStore';
import { MultiSelectDropdownWithField } from '@/components/ui/dropdowns';
import { BaseBasicInfoProps } from './types';
import { OwnerAndPermissions } from './OwnerAndPermissions';
import { DialogVariableDeclarations } from '@/components/wizards/dialog/DialogVariableDeclarations';

export function BasicInfoDialog(props: BaseBasicInfoProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();

  const {
    editedData,
    setEditedData,
    isEditing,
    setIsEditing,
    isSaving,
    availableStatuses,
    permissions,
    currentItem,
    onSave,
    onCancel,
  } = props;

  // Use ref to track latest editedData without causing callback recreation
  const editedDataRef = useRef(editedData);
  useEffect(() => {
    editedDataRef.current = editedData;
  }, [editedData]);

  // Memoized callback for updating dialog variables
  const handleUpdateVariables = useCallback((variables: any[]) => {
    if (!isEditing) setIsEditing(true);
    const currentData = editedDataRef.current;
    if (currentData) {
      const updated = { ...currentData, dialogVariables: variables };
      setEditedData(updated);
    }
  }, [isEditing, setIsEditing, setEditedData]);

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Basic Information
            </h3>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="basic-info-description" className="text-sm">
              {t('platform.common.description')}
            </Label>
            <TextAreaElement
              value={editedData?.description || ''}
              onChange={(value) => {
                if (!isEditing) setIsEditing(true);
                setEditedData({ ...editedData, description: value });
              }}
              placeholder={t('platform.common.enter_object_description')}
              disabled={!permissions.canEdit}
              rows={3}
              className="mt-1"
            />
          </div>



          {/* Status */}
          {Object.keys(availableStatuses).length > 0 && (
            <div className="relative z-[100]">
              <Label htmlFor="basic-info-status" className="text-sm">Status</Label>
              <Select
                value={editedData?.status || Object.keys(availableStatuses)[0]}
                onValueChange={(value) => {
                  if (!isEditing) setIsEditing(true);
                  setEditedData({ ...editedData, status: value });
                }}
                disabled={!permissions.canEdit}
              >
                <SelectTrigger className="mt-1 border border-grey-G5 rounded p-2 text-sm font-medium text-grey-G2">
                  <SelectValue placeholder="Select status">
                    {editedData?.status && availableStatuses[editedData.status] && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full border border-border flex-shrink-0" 
                          style={{ backgroundColor: availableStatuses[editedData.status].color }}
                        />
                        <span className="capitalize">{editedData.status}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
                  {Object.entries(availableStatuses).map(([statusKey, statusData]) => (
                    <SelectItem key={statusKey} value={statusKey}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full border border-border flex-shrink-0"
                          style={{ backgroundColor: statusData.color }}
                        />
                        <span className="capitalize">{statusKey}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Owner and Edit Permissions */}
          <OwnerAndPermissions
            owner={editedData?.owner || null}
            editPermissions={editedData?.edit_permissions || 'Open'}
            onOwnerChange={(owner) => {
              if (!isEditing) setIsEditing(true);
              setEditedData({ ...editedData, owner });
            }}
            onPermissionsChange={(permissions) => {
              if (!isEditing) setIsEditing(true);
              setEditedData({ ...editedData, edit_permissions: permissions });
            }}
            disabled={!permissions.canEdit}
            selectedGameId={selectedGame?.id}
          />

          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide py-2">
            Dialog Settings
          </h3>

          {/* Starting Section */}
          {currentItem && (currentItem as any).data?.sections && (
            <div className="relative z-[100]">
              <Label htmlFor="basic-info-starting-section" className="text-sm ">
                Starting Section
              </Label>
              <Select
                value={editedData?.startingSection || '__none__'}
                onValueChange={(value) => {
                  if (!isEditing) setIsEditing(true);
                  setEditedData({
                    ...editedData,
                    startingSection: value === '__none__' ? undefined : value
                  });
                }}
                disabled={!permissions.canEdit}
              >
                <SelectTrigger className=" border border-border rounded p-2 w-full">
                  <SelectValue placeholder="Select starting section (optional)" />
                </SelectTrigger>
                <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
                  <SelectItem value="__none__">None (use first section)</SelectItem>
                  {(currentItem as any).data.sections.map((section: any) => (
                    <SelectItem key={section.section_identifier} value={section.section_identifier}>
                      {section.name || section.section_identifier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                The section where the dialog begins when started
              </p>
            </div>
          )}

          {/* Translatable */}
          <div className="flex items-center space-x-2 py-4">
            <Checkbox
              id="basic-info-translatable"
              checked={editedData?.translatable !== false}
              onCheckedChange={(checked) => {
                if (!isEditing) setIsEditing(true);
                setEditedData({ ...editedData, translatable: checked === true });
              }}
              disabled={!permissions.canEdit}
            />
            <Label htmlFor="basic-info-translatable" className="text-sm cursor-pointer">
              Add all texts to Translation Management System (TMS)
            </Label>
          </div>

          {/* Speaker Mode */}
          <div className="space-y-3 ">
            <div className="relative z-[100]">
              <Label htmlFor="basic-info-speaker-mode" className="text-sm font-medium">
                Character/Speaker Attribution
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Configure how characters are assigned to dialog text
              </p>
              <Select
                value={editedData?.speakerMode || 'none'}
                onValueChange={(value: any) => {
                  if (!isEditing) setIsEditing(true);
                  setEditedData({ ...editedData, speakerMode: value });
                }}
                disabled={!permissions.canEdit}
              >
                <SelectTrigger id="basic-info-speaker-mode" className="mt-1 border border-border rounded p-2 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
                  <SelectItem value="none">None - No speaker attribution</SelectItem>
                  <SelectItem value="free-text">Free Text - Type any name</SelectItem>
                  <SelectItem value="type">Type - Select from a type list</SelectItem>
                  <SelectItem value="object">Object - Select from templates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Speaker Type Selector */}
            {editedData?.speakerMode === 'type' && selectedGame?.id && (
              <div className="relative z-[100]">
                <Label htmlFor="basic-info-speaker-type" className="text-sm">
                  Select Type *
                </Label>
                <Select 
                  value={editedData?.speakerTypeId || '__none__'} 
                  onValueChange={(value) => {
                    if (!isEditing) setIsEditing(true);
                    setEditedData({ 
                      ...editedData, 
                      speakerTypeId: value === '__none__' ? null : value 
                    });
                  }}
                  disabled={!permissions.canEdit}
                >
                  <SelectTrigger id="basic-info-speaker-type" className="mt-1">
                    <SelectValue placeholder="Choose a type..." />
                  </SelectTrigger>
                  <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
                    <SelectItem value="__none__">None</SelectItem>
                    {getCachedTypes(selectedGame.id).map((type: any) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Speaker Template Selector */}
            {editedData?.speakerMode === 'object' && selectedGame?.id && (
              <div>
                <Label className="text-sm">Select Template(s) *</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Characters can be selected from objects using these templates
                </p>
                <MultiSelectDropdownWithField
                  value={editedData?.speakerTemplateIds || []}
                  onChange={(value) => {
                    if (!isEditing) setIsEditing(true);
                    setEditedData({
                      ...editedData,
                      speakerTemplateIds: Array.isArray(value) ? value : [value]
                    });
                  }}
                  options={getCachedTemplates(selectedGame.id)
                    .filter(t => t.type === 'glossary' || !t.type)
                    .map((template: any) => ({ id: template.id, label: template.name }))}
                  placeholder="Select templates..."
                  multiple
                  disabled={!permissions.canEdit}
                />
              </div>
            )}
          </div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide py-2">
            Variables
          </h3>
          {/* Dialog Variables Editor */}
          <div className="">
            <DialogVariableDeclarations
              variables={editedData?.dialogVariables || []}
              onUpdateVariables={handleUpdateVariables}
            />
          </div>

        </div>
      </div>

      {/* Save/Cancel buttons */}
      {permissions.canEdit && isEditing && (
        <div className="p-3 border-t border-border bg-muted flex gap-2">
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1"
            variant="default"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button
            onClick={onCancel}
            disabled={isSaving}
            variant="outline"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
